import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import {DateTimePicker} from "@mui/x-date-pickers";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {useTranslation} from "react-i18next";

import {AMENDMENT_DIRECTION} from "../../helpers/ShiftHelper";
import {
    AMENDMENT_ATTACHMENT_SCOPES,
    AMENDMENT_DURATION_UNITS,
} from "../../domain/AmendmentConstants";

const styles = {
    sectionLabel: {
        color: "text.secondary",
        fontSize: "0.8rem",
        fontWeight: 600,
        letterSpacing: "0.02em"
    },
    toggleButton: {
        textTransform: 'none'
    },
    compactToggleGroup: {
        "& .MuiToggleButton-root": {
            paddingY: "0.42rem",
        },
    },
    attachmentOption: {
        alignItems: "center",
        display: "flex",
        gap: "0.35rem",
    },
    attachmentIcon: {
        transform: "rotate(90deg)",
    },
    directionOption: {
        alignItems: "center",
        display: "flex",
        gap: "0.45rem",
    },
    directionDot: {
        borderRadius: "50%",
        display: "inline-block",
        height: "0.55rem",
        width: "0.55rem",
    },
    deductionDot: {
        backgroundColor: "error.main",
    },
    additionDot: {
        backgroundColor: "success.main",
    },
    timeAndDurationRow: {
        display: "flex",
        justifyContent: "space-between",
    },
    durationRow: {
        display: 'flex',
        gap: "2rem",
    },
    durationField: {
        width: "7rem",
    },
    durationUnit: {
        minWidth: "7rem",
    },
};

export default function AmendmentDetailsForm({values, shiftOptions, actions}) {
    const {t} = useTranslation();
    const amendmentTypeLabels = t("shiftAdminAddAmendmentDialog.form.amendmentTypes", {returnObjects: true}) || {};
    const amendmentTypeOptions = Object.entries(amendmentTypeLabels).map(([value, label]) => ({
        value,
        label,
    }));
    const isAttachedAmendment = values.amendmentScope === AMENDMENT_ATTACHMENT_SCOPES.ATTACHED;
    const isStandaloneAmendment = !isAttachedAmendment;
    const hasNoAvailableShifts = shiftOptions.length === 0;

    return (
        <Stack spacing={2}>
            <Box>
                <Typography sx={styles.sectionLabel}>{t("shiftAdminAddAmendmentDialog.form.amendmentScope")}</Typography>
                <ToggleButtonGroup
                    color="primary"
                    value={values.amendmentScope}
                    exclusive
                    onChange={actions.handleScopeChange}
                    sx={styles.compactToggleGroup}
                    fullWidth>
                    <ToggleButton
                        sx={styles.toggleButton}
                        value={AMENDMENT_ATTACHMENT_SCOPES.ATTACHED}
                        disabled={hasNoAvailableShifts}>
                        <Tooltip title={t("shiftAdminAddAmendmentDialog.form.scope.attached.tooltip")} arrow>
                            <Box sx={styles.attachmentOption}>
                                <AttachFileIcon sx={styles.attachmentIcon}/>
                                {t("shiftAdminAddAmendmentDialog.form.scope.attached.label")}
                            </Box>
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton sx={styles.toggleButton} value={AMENDMENT_ATTACHMENT_SCOPES.STANDALONE}>
                        <Tooltip title={t("shiftAdminAddAmendmentDialog.form.scope.standalone.tooltip")} arrow>
                            <Box sx={styles.attachmentOption}>
                                <AddIcon/>
                                {t("shiftAdminAddAmendmentDialog.form.scope.standalone.label")}
                            </Box>
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <FormControl fullWidth size="small" disabled={isStandaloneAmendment || hasNoAvailableShifts}>
                <InputLabel id="amendment-shift-label">{t("shiftAdminAddAmendmentDialog.form.shift")}</InputLabel>
                <Select
                    labelId="amendment-shift-label"
                    value={values.selectedShiftId ?? ""}
                    label={t("shiftAdminAddAmendmentDialog.form.shift")}
                    onChange={actions.handleShiftChange}
                    MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                    {
                        shiftOptions.length === 0
                        &&
                        <MenuItem>
                            {t("shiftAdminAddAmendmentDialog.form.noShiftsAvailable")}
                        </MenuItem>
                    }
                    {shiftOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth size="small">
                <InputLabel id="amendment-type-label">{t("shiftAdminAddAmendmentDialog.form.amendmentType")}</InputLabel>
                <Select
                    labelId="amendment-type-label"
                    value={values.amendmentType ?? ""}
                    label={t("shiftAdminAddAmendmentDialog.form.amendmentType")}
                    onChange={actions.handleAmendmentTypeChange}
                    MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                    {amendmentTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box>
                <Typography sx={styles.sectionLabel}>{t("shiftAdminAddAmendmentDialog.form.direction")}</Typography>
                <ToggleButtonGroup
                    color="primary"
                    value={values.amendmentDirection}
                    exclusive
                    onChange={actions.handleDirectionChange}
                    sx={styles.compactToggleGroup}
                    fullWidth>
                    <ToggleButton sx={styles.toggleButton} value={AMENDMENT_DIRECTION.DEBIT}>
                        <Box sx={styles.directionOption}>
                            <Box component="span" sx={[styles.directionDot, styles.deductionDot]}/>
                            {t("shiftAdminAddAmendmentDialog.form.deduction")}
                        </Box>
                    </ToggleButton>
                    <ToggleButton sx={styles.toggleButton} value={AMENDMENT_DIRECTION.CREDIT}>
                        <Box sx={styles.directionOption}>
                            <Box component="span" sx={[styles.directionDot, styles.additionDot]}/>
                            {t("shiftAdminAddAmendmentDialog.form.addition")}
                        </Box>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={styles.timeAndDurationRow}>
                <DateTimePicker
                    label={t("shiftAdminAddAmendmentDialog.form.incidentTime")}
                    value={values.incidentTime}
                    onChange={actions.handleIncidentTimeChange}
                    disabled={isAttachedAmendment}
                    ampm={false}
                    slotProps={{
                        textField: {
                            size: "small"
                        }
                    }}
                    renderInput={(params) => <TextField {...params} size="small"/>}
                    disableMaskedInput={true}/>

                <Box sx={styles.durationRow}>
                    <TextField
                        size="small"
                        type="number"
                        label={t("shiftAdminAddAmendmentDialog.form.duration")}
                        value={values.durationValue}
                        onChange={actions.handleDurationValueChange}
                        inputProps={{min: 1}}
                        sx={styles.durationField}/>

                    <FormControl size="small" sx={styles.durationUnit}>
                        <InputLabel id="duration-unit-label">{t("shiftAdminAddAmendmentDialog.form.unit")}</InputLabel>
                        <Select
                            labelId="duration-unit-label"
                            label={t("shiftAdminAddAmendmentDialog.form.unit")}
                            value={values.durationUnit}
                            onChange={actions.handleDurationUnitChange}
                            MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                            <MenuItem value={AMENDMENT_DURATION_UNITS.MINUTES}>{t("shiftAdminAddAmendmentDialog.form.durationUnitMinutes")}</MenuItem>
                            <MenuItem value={AMENDMENT_DURATION_UNITS.HOURS}>{t("shiftAdminAddAmendmentDialog.form.durationUnitHours")}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <TextField
                fullWidth
                label={t("shiftAdminAddAmendmentDialog.form.reasonOrNote")}
                required
                multiline
                minRows={2}
                placeholder={t("shiftAdminAddAmendmentDialog.form.reasonOrNotePlaceholder")}
                value={values.notes}
                onChange={actions.handleNotesChange}/>
        </Stack>
    );
}